import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/axios";
import "./AgentCreation.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebarnew from "./NewUI/Sidebarnew";
import Navigationnew from "./NewUI/Navigationnew";

const toastSuccess = (msg) =>
  toast.success(msg, {
    position: "top-right",
    autoClose: 2500,
    hideProgressBar: true,
    style: {
      background: "#FAFAFA",
      color: "#1783ef",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
      border: "1px solid #DBEAFE",
    },
  });

const toastError = (msg) =>
  toast.error(msg, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: true,
    style: {
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
    },
  });

const toastDeploy = () =>
  toast.success("Agent deployed successfully!", {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: true,
    style: {
      background: "#FAFAFA",
      color: "#10B981",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
      border: "1px solid #D1FAE5",
    },
  });

const AgentCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("Basic Information");
  const [agentId, setAgentId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [permissions, setPermissions] = useState({ canWriteAgent: true });
  const [hasChanges, setHasChanges] = useState(false);
  const [basicInfoTouched, setBasicInfoTouched] = useState(false);

  const [sourceToggles, setSourceToggles] = useState({
    manual: true,
    drive: true,
    zapier: true,
    url: true,
  });

  const [selectedSource, setSelectedSource] = useState("All");

  const [docFilters, setDocFilters] = useState({
    type: "All",
    department: "All",
    status: "All",
  });

  const [editNodeOpen, setEditNodeOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeName, setNodeName] = useState("");

  const [showKbModal, setShowKbModal] = useState(false);
  const [kbDocuments, setKbDocuments] = useState([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbError, setKbError] = useState(null);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [attachedDocs, setAttachedDocs] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    provider: "",
    model: "",
    temperature: 0.5,
    description: "",
    system_prompt: "",
    tone: "Formal",
    verbosity: "Medium",
    prompt: "Prompt",
  });

  const tabs = ["Basic Information", "Prompt", "Knowledge Base"];
  const [llmConfig, setLlmConfig] = useState({});

  useEffect(() => {
    const loadInitialData = async () => {
      setPageLoading(true);
      await Promise.all([fetchModels()]);
      setPageLoading(false);
    };
    loadInitialData();
  }, []);

  const fetchModels = async () => {
    try {
      const res = await api.get("/llm-models");
      if (res.data) setLlmConfig(res.data);
    } catch (err) {
      console.error("Failed to fetch LLM models", err);
    }
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await api.get("/user-roles/me");
        const role = res.data;
        if (role.isAdmin) {
          setPermissions({ canWriteAgent: true });
          return;
        }
        if (role && role.write_access && !role.noRoleDefined) {
          setPermissions({
            canWriteAgent:
              role.write_access.includes("Agent") ||
              (role.both_access && role.both_access.includes("Agent")),
          });
        }
      } catch {
        console.error("Permission check failed");
      }
    };
    fetchPermissions();
  }, []);

  const isLastTab = activeTab === tabs[tabs.length - 1];
  const canDeployNow = attachedDocs.length > 0;

  const fetchKbDocuments = async () => {
    setKbLoading(true);
    setKbError(null);
    try {
      const res = await api.get("/documents", {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      setKbDocuments(res.data.documents || []);
    } catch (err) {
      console.error("Failed to fetch KB documents:", err);
      setKbError("Failed to load documents. Please try again.");
    } finally {
      setKbLoading(false);
    }
  };

  const handleOpenKbModal = () => {
    setSelectedDocs(attachedDocs.map((doc) => doc.id));
    fetchKbDocuments();
    setShowKbModal(true);
  };

  const handleCloseKbModal = () => {
    setShowKbModal(false);
    setSelectedDocs([]);
    setKbError(null);
  };

  const handleToggleDoc = (docId) => {
    setSelectedDocs((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const handleAttachDocs = () => {
    const docsToAttach = kbDocuments.filter((doc) => selectedDocs.includes(doc.id));
    setAttachedDocs(docsToAttach);
    markTabChanged();
    handleCloseKbModal();
  };

  const markTabChanged = () => setHasChanges(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "provider" ? { model: "" } : {}),
    }));
    markTabChanged();
  };

  const handleTabClick = (tab) => {
    if (isSaving) return;
    setActiveTab(tab);
  };

  const handleSourceFilterChange = (e) => {
    const val = e.target.value;
    setSelectedSource(val);
    setSourceToggles({
      manual: val === "All" || val === "manual",
      drive: val === "All" || val === "drive",
      zapier: val === "All" || val === "zapier",
      url: val === "All" || val === "url",
    });
    markTabChanged();
  };

  const buildPayload = async (status = "draft") => {
    const wsRes = await api.get("/workspaces/check");
    const workspaceData = wsRes.data.workspace;
    if (!workspaceData) throw new Error("No workspace found. Please create a workspace first.");
    return {
      ...formData,
      workspaceId: String(workspaceData.id),
      attachedDocIds: attachedDocs.map((doc) => doc.id || doc._id),
      status,
    };
  };

  const handleSaveAndExit = async () => {
    try {
      const payload = await buildPayload("draft");
      if (agentId) {
        await api.put(`/agents/${agentId}`, payload);
      } else {
        const res = await api.post("/agents", payload);
        setAgentId(res.data.id);
      }
      setHasChanges(false);
      toastSuccess("Agent saved successfully!");
      setTimeout(() => navigate("/home"), 800);
    } catch (err) {
      toastError("Save failed: " + (err.response?.data?.message || err.message));
    }
  };

  const handleNext = async () => {
    if (isSaving) return;
    const currentIndex = tabs.indexOf(activeTab);
    setIsSaving(true);
    try {
      if (activeTab === "Basic Information" && !agentId && basicInfoTouched) {
        const payload = await buildPayload("draft");
        const res = await api.post("/agents", payload);
        setAgentId(res.data.id);
        toastSuccess("Agent created successfully!");
      }
      if (isLastTab) {
        const payload = await buildPayload("draft");
        if (agentId) {
          await api.put(`/agents/${agentId}`, payload);
        } else {
          const res = await api.post("/agents", payload);
          setAgentId(res.data.id);
          toastSuccess("Agent created successfully!");
        }
        setHasChanges(false);
        toastSuccess("Agent saved successfully!");
        setTimeout(() => navigate("/home"), 800);
      } else {
        const payload = await buildPayload("draft");
        if (agentId) await api.put(`/agents/${agentId}`, payload);
        setActiveTab(tabs[currentIndex + 1]);
        setHasChanges(false);
      }
    } catch (err) {
      toastError("Save failed: " + (err.response?.data?.message || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeploy = async () => {
    if (!permissions.canWriteAgent) return;
    try {
      const payload = await buildPayload("active");
      if (agentId) {
        await api.put(`/agents/${agentId}`, payload);
      } else {
        await api.post("/agents", payload);
      }
      toastDeploy();
      setHasChanges(false);
      setTimeout(() => navigate("/home"), 900);
    } catch (err) {
      toastError("Deploy failed: " + (err.response?.data?.message || err.message));
    }
  };

  const handleRemoveDoc = (docId) => {
    setAttachedDocs((prev) => prev.filter((doc) => (doc.id || doc._id) !== docId));
    markTabChanged();
  };

  const applyFilters = (docsArray) => {
    return docsArray.filter((doc) => {
      const src = doc.source_type || doc.source || "";
      const fType = (doc.file_type || "").toLowerCase();
      const docDep = doc.department ? doc.department.trim() : "None";
      const isVerified = doc.verified === true;

      let sourceMatch = false;
      if (sourceToggles.manual && src === "kb") sourceMatch = true;
      if (sourceToggles.drive && src === "google_drive") sourceMatch = true;
      if (sourceToggles.zapier && src === "zapier_crm") sourceMatch = true;
      if (sourceToggles.url && src === "url_scrape") sourceMatch = true;
      if (!sourceMatch) return false;

      if (docFilters.type !== "All") {
        if (docFilters.type === "PDF" && !fType.includes("pdf")) return false;
        if (docFilters.type === "Word" && !fType.includes("word") && !fType.includes("docx") && !fType.includes("doc")) return false;
        if (docFilters.type === "URL" && !fType.includes("url")) return false;
        if (docFilters.type === "Other" && (fType.includes("pdf") || fType.includes("word") || fType.includes("doc") || fType.includes("url"))) return false;
      }

      if (docFilters.department !== "All") {
        if (docDep !== docFilters.department) return false;
      }

      if (docFilters.status !== "All") {
        if (docFilters.status === "Verified" && !isVerified) return false;
        if (docFilters.status === "Not Verified" && isVerified) return false;
      }

      return true;
    });
  };

  const allKnownDocs = [...attachedDocs, ...kbDocuments];
  const availableDepartments = [
    ...new Set(allKnownDocs.map((d) => (d.department ? d.department.trim() : "None"))),
  ].filter(Boolean);

  const filteredAttachedDocs = applyFilters(attachedDocs);
  const filteredModalDocs = applyFilters(kbDocuments);

  return (
    <div className="db-page-layout">
  

      {/* SIDEBAR */}
      <aside className="db-sidebar-shell">
        <Sidebarnew />
      </aside>

      {/* MAIN */}
      <div className="db-main-wrapper">
        <div className="db-topbar-shell">
          <Navigationnew
            setArtifactsOpen={() => {}}
            artifactsOpen={false}
          />
        </div>
         {/* ADD THIS */}
  <div className="create-agent-header">
    <h1>Create New Agent</h1>
    <p>Give basic information to create new agent</p>
  </div>

        <div className="agent-creation-container w-100">
          <div className="container-fluid p-4">

            {/* Main Card */}
            <div className="form-card shadow-sm w-100" style={{ overflow: "visible" }}>
              <div className="tabs-header-bar d-flex">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    className={`tab-link-blended ${activeTab === tab ? "active" : ""}`}
                    onClick={() => handleTabClick(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="form-content-body" style={{ overflow: "visible" }}>

                {/* TAB 1 — BASIC INFORMATION */}
                {activeTab === "Basic Information" && pageLoading ? (
                  <div key="basic-skeleton" className="tab-panel-enter p-4 placeholder-glow">
                    <div className="row">
                      <div className="col-md-6 d-flex flex-column gap-4">
                        {[...Array(4)].map((_, i) => (
                          <div key={i}>
                            <label className="form-label-custom placeholder col-3"></label>
                            <div className="form-control input-background placeholder col-12" style={{ height: "38px" }}></div>
                          </div>
                        ))}
                      </div>
                      <div className="col-md-6 d-flex flex-column gap-4">
                        <div>
                          <label className="form-label-custom placeholder col-3"></label>
                          <div className="form-control input-background placeholder col-12" style={{ height: "38px" }}></div>
                        </div>
                        <div className="d-flex flex-column flex-grow-1">
                          <label className="form-label-custom placeholder col-3"></label>
                          <div className="form-control input-background placeholder col-12 flex-grow-1" style={{ minHeight: "110px" }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : activeTab === "Basic Information" && (
                  <div key="basic" className="tab-panel-enter p-4" style={{ overflow: "visible" }}>
                    <div className="row">
                      <div className="col-md-6 d-flex flex-column gap-4">
                        <div>
                          <label className="form-label-custom">Agent Name</label>
                          <input
                            type="text"
                            name="name"
                            className="form-control input-background"
                            placeholder="Sarah Johnson"
                            value={formData.name}
                            onChange={(e) => {
                              handleInputChange(e);
                              setBasicInfoTouched(e.target.value.length > 0);
                            }}
                            disabled={!permissions.canWriteAgent}
                          />
                        </div>
                        <div>
                          <label className="form-label-custom">LLM Model</label>
                          <select
                            name="provider"
                            className="form-select input-background"
                            value={formData.provider}
                            onChange={handleInputChange}
                            disabled={!permissions.canWriteAgent}
                          >
                            <option value="" disabled hidden>Select Provider</option>
                            {Object.keys(llmConfig || {}).map((provider) => (
                              <option key={provider} value={provider}>{provider}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ position: "relative" }}>
                          <label className="form-label-custom">Model Version</label>
                          <div
                            className="form-control input-background d-flex justify-content-between align-items-center"
                            style={{
                              cursor: !permissions.canWriteAgent || !formData.provider ? "not-allowed" : "pointer",
                              opacity: !permissions.canWriteAgent || !formData.provider ? 0.6 : 1,
                              userSelect: "none",
                            }}
                            onClick={() => {
                              if (!permissions.canWriteAgent || !formData.provider) return;
                              setModelDropdownOpen((prev) => !prev);
                            }}
                          >
                            <span style={{ color: formData.model ? "#1f2937" : "#9ca3af" }}>
                              {formData.model || "Select Model"}
                            </span>
                            <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>▼</span>
                          </div>

                          {modelDropdownOpen && (
                            <>
                              <div
                                style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                                onClick={() => setModelDropdownOpen(false)}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  background: "#fff",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "6px",
                                  zIndex: 1000,
                                  maxHeight: "220px",
                                  overflowY: "auto",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  marginTop: "2px",
                                }}
                              >
                                {(llmConfig?.[formData.provider] || []).map((model) => (
                                  <div
                                    key={model}
                                    style={{
                                      padding: "9px 14px",
                                      fontSize: "0.875rem",
                                      cursor: "pointer",
                                      background: formData.model === model ? "#eff6ff" : "transparent",
                                      color: formData.model === model ? "#2563eb" : "#374151",
                                    }}
                                    onMouseEnter={(e) => { if (formData.model !== model) e.target.style.background = "#f9fafb"; }}
                                    onMouseLeave={(e) => { if (formData.model !== model) e.target.style.background = "transparent"; }}
                                    onClick={() => {
                                      handleInputChange({ target: { name: "model", value: model } });
                                      setModelDropdownOpen(false);
                                    }}
                                  >
                                    {model}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        <div>
                          <label className="form-label-custom">Temperature</label>
                          <input
                            type="range"
                            name="temperature"
                            className="slider-blue w-100 mt-2"
                            min="0"
                            max="1"
                            step="0.1"
                            value={formData.temperature}
                            onChange={handleInputChange}
                            disabled={!permissions.canWriteAgent}
                            style={{
                              background: `linear-gradient(to right, #2563EB 0%, #2563EB ${formData.temperature * 100}%, #E5E7EB ${formData.temperature * 100}%, #E5E7EB 100%)`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="col-md-6 d-flex flex-column gap-4">
                        <div>
                          <label className="form-label-custom">Type</label>
                          <input
                            type="text"
                            name="type"
                            className="form-control input-background"
                            placeholder="HR Recruiter"
                            value={formData.type}
                            onChange={handleInputChange}
                            disabled={!permissions.canWriteAgent}
                          />
                        </div>
                        <div className="d-flex flex-column flex-grow-1">
                          <label className="form-label-custom">Description</label>
                          <textarea
                            name="description"
                            className="form-control input-background flex-grow-1"
                            style={{ minHeight: "110px", resize: "none" }}
                            placeholder="Description"
                            value={formData.description}
                            onChange={handleInputChange}
                            disabled={!permissions.canWriteAgent}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2 — PROMPT */}
                {activeTab === "Prompt" && pageLoading ? (
                  <div key="prompt-skeleton" className="tab-panel-enter p-4 placeholder-glow">
                    <div className="row">
                      <div className="col-md-4 d-flex flex-column gap-4">
                        {[...Array(2)].map((_, i) => (
                          <div key={i}>
                            <label className="form-label-custom placeholder col-4"></label>
                            <div className="form-select input-background placeholder col-12" style={{ height: "38px" }}></div>
                          </div>
                        ))}
                      </div>
                      <div className="col-md-8 d-flex flex-column">
                        <label className="form-label-custom placeholder col-3"></label>
                        <div className="form-control border-0 input-background placeholder col-12 flex-grow-1" style={{ minHeight: "140px", resize: "none" }}></div>
                      </div>
                    </div>
                  </div>
                ) : activeTab === "Prompt" && (
                  <div key="prompt" className="tab-panel-enter p-4">
                    <div className="row">
                      <div className="col-md-4 d-flex flex-column gap-4">
                        <div>
                          <label className="form-label-custom">Tone</label>
                          <select
                            name="tone"
                            className="form-select input-background prompt-dropdown"
                            value={formData.tone}
                            onChange={handleInputChange}
                            disabled={!permissions.canWriteAgent}
                          >
                            <option>Formal</option>
                            <option>Neutral</option>
                            <option>Friendly</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label-custom">Verbosity</label>
                          <select
                            name="verbosity"
                            className="form-select input-background prompt-dropdown"
                            value={formData.verbosity}
                            onChange={handleInputChange}
                            disabled={!permissions.canWriteAgent}
                          >
                            <option>Low</option>
                            <option>Medium</option>
                            <option>High</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-8 d-flex flex-column">
                        <label className="form-label-custom">System Prompt</label>
                        <textarea
                          name="system_prompt"
                          className="form-control border-0 input-background flex-grow-1"
                          style={{ minHeight: "140px", resize: "none" }}
                          placeholder="Description"
                          value={formData.system_prompt}
                          onChange={handleInputChange}
                          disabled={!permissions.canWriteAgent}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 3 — KNOWLEDGE BASE */}
                {activeTab === "Knowledge Base" && pageLoading ? (
                  <div key="kb-skeleton" className="tab-panel-enter d-flex flex-column h-100 placeholder-glow">
                    <div className="d-flex justify-content-between align-items-end px-4 py-3 border-bottom bg-white">
                      <div className="d-flex gap-3">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} style={{ width: "170px" }}>
                            <div className="d-block mb-1 text-muted form-label-custom placeholder col-6"></div>
                            <div className="form-select bg-white border placeholder col-12" style={{ height: "38px" }}></div>
                          </div>
                        ))}
                      </div>
                      <div className="d-flex align-items-end gap-3">
                        <div style={{ width: "170px" }}>
                          <div className="d-block mb-1 text-muted form-label-custom placeholder col-6"></div>
                          <div className="form-select bg-white border placeholder col-12" style={{ height: "38px" }}></div>
                        </div>
                        <button className="btn-solid-blue placeholder col-4" style={{ height: "38px" }}></button>
                      </div>
                    </div>
                    <div className="kb-table-scroll-container bg-white">
                      <table className="table kb-custom-table align-middle mb-0">
                        <thead>
                          <tr>
                            {[...Array(7)].map((_, i) => (
                              <th key={i}><div className="placeholder col-8"></div></th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[...Array(5)].map((_, index) => (
                            <tr key={index}>
                              {[...Array(7)].map((_, i) => (
                                <td key={i}><div className="placeholder col-10"></div></td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : activeTab === "Knowledge Base" && (
                  <div key="kb" className="tab-panel-enter d-flex flex-column h-100">
                    <div className="d-flex justify-content-between align-items-end px-4 py-3 border-bottom bg-white">
                      <div className="d-flex gap-3">
                        <div style={{ width: "170px" }}>
                          <label className="d-block mb-1 text-muted form-label-custom">Type</label>
                          <select
                            className="form-select bg-white border"
                            value={docFilters.type}
                            onChange={(e) => setDocFilters((prev) => ({ ...prev, type: e.target.value }))}
                          >
                            <option value="All">All Types</option>
                            <option value="PDF">PDF</option>
                            <option value="Word">Word Document</option>
                            <option value="URL">URL / Web Data</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div style={{ width: "170px" }}>
                          <label className="d-block mb-1 text-muted form-label-custom">Department</label>
                          <select
                            className="form-select bg-white border"
                            value={docFilters.department}
                            onChange={(e) => setDocFilters((prev) => ({ ...prev, department: e.target.value }))}
                          >
                            <option value="All">All Departments</option>
                            {availableDepartments.map((dep) => (
                              <option key={dep} value={dep}>{dep}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ width: "170px" }}>
                          <label className="d-block mb-1 text-muted form-label-custom">Status</label>
                          <select
                            className="form-select bg-white border"
                            value={docFilters.status}
                            onChange={(e) => setDocFilters((prev) => ({ ...prev, status: e.target.value }))}
                          >
                            <option value="All">All Status</option>
                            <option value="Verified">Verified</option>
                            <option value="Not Verified">Not Verified</option>
                          </select>
                        </div>
                      </div>
                      <div className="d-flex align-items-end gap-3">
                        <div style={{ width: "170px" }}>
                          <label className="d-block mb-1 text-muted form-label-custom">Source Integration</label>
                          <select
                            className="form-select bg-white border"
                            value={selectedSource}
                            onChange={handleSourceFilterChange}
                            disabled={!permissions.canWriteAgent}
                          >
                            <option value="All">All Sources</option>
                            <option value="manual">Manual</option>
                            <option value="drive">Drive</option>
                            <option value="zapier">Zapier</option>
                            <option value="url">URL Scraping</option>
                          </select>
                        </div>
                        {permissions.canWriteAgent && (
                          <button className="btn-solid-blue" onClick={handleOpenKbModal}>Add New</button>
                        )}
                      </div>
                    </div>

                    <div className="kb-table-scroll-container bg-white">
                      <table className="table kb-custom-table align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Document Name</th>
                            <th>Department</th>
                            <th>Source</th>
                            <th>Version</th>
                            <th>Verification Status</th>
                            <th>Last updated</th>
                            <th style={{ textAlign: "center" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAttachedDocs.length > 0 ? (
                            filteredAttachedDocs.map((doc) => (
                              <tr key={doc.id || doc._id}>
                                <td className="text-dark fw-medium">
                                  <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-robot text-primary fs-5"></i>
                                    {doc.file_name || doc.name || "Untitled"}
                                  </div>
                                </td>
                                <td>{doc.department || "-"}</td>
                                <td><span className="source-badge">{doc.source_type || doc.source || "MANUAL"}</span></td>
                                <td>{doc.version || "v1.0"}</td>
                                <td>
                                  <span className={doc.verified ? "status-verified" : "status-not-verified"}>
                                    {doc.verified ? "VERIFIED" : "NOT VERIFIED"}
                                  </span>
                                </td>
                                <td>{doc.updated || "Just Now"}</td>
                                <td style={{ textAlign: "center" }}>
                                  <div className="d-flex align-items-center justify-content-center">
                                    <i
                                      className="bi bi-trash text-danger"
                                      style={{ cursor: "pointer", fontSize: "1.1rem" }}
                                      onClick={() => handleRemoveDoc(doc.id || doc._id)}
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-5 text-muted fst-italic">
                                {attachedDocs.length === 0
                                  ? "No documents attached. Please click 'Add New' to attach documents."
                                  : "No attached documents match the current filters."}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER BUTTONS */}
            <div className="d-flex justify-content-end align-items-center gap-3 mt-3 mb-1">
              <button
                className="btn-save-exit"
                onClick={handleSaveAndExit}
                disabled={!basicInfoTouched}
                title={!basicInfoTouched ? "Enter agent name first" : ""}
              >
                Save & Exit
              </button>

              {permissions.canWriteAgent && (
                <>
                  <button
                    className="btn-save-next"
                    onClick={handleNext}
                    disabled={!basicInfoTouched || isSaving}
                    title={!basicInfoTouched ? "Enter agent name first" : ""}
                  >
                    {isSaving ? "Saving..." : isLastTab ? "Save & Finish" : "Save & Next"}
                  </button>

                  <button
                    className="btn-deploy-agent"
                    onClick={handleDeploy}
                    disabled={!canDeployNow}
                    style={{ opacity: canDeployNow ? 1 : 0.4, cursor: canDeployNow ? "pointer" : "not-allowed" }}
                    title={!canDeployNow ? "Attach at least one document in Knowledge Base to deploy" : "Deploy Agent"}
                  >
                    Deploy Agent
                  </button>
                </>
              )}
            </div>

          </div>{/* end container-fluid */}

          {/* KB MODAL */}
          {showKbModal && (
            <div className="kb-modal-backdrop" onClick={handleCloseKbModal}>
              <div className="kb-modal-box" onClick={(e) => e.stopPropagation()}>
                <div className="kb-modal-header">
                  <div>
                    <h6 className="kb-modal-title">Select Documents</h6>
                    <p className="kb-modal-subtitle">Choose documents from your Knowledge Base to attach to this agent</p>
                  </div>
                  <span className="kb-modal-close" onClick={handleCloseKbModal}>×</span>
                </div>

                <div className="kb-modal-body">
                  {kbLoading && (
                    <div className="kb-modal-state">
                      <div className="spinner-border text-primary" role="status"></div>
                      <p className="mt-3 text-muted small">Loading documents...</p>
                    </div>
                  )}
                  {kbError && !kbLoading && (
                    <div className="kb-modal-state">
                      <p className="text-danger small">{kbError}</p>
                      <button className="btn btn-sm btn-outline-primary mt-2" onClick={fetchKbDocuments}>Retry</button>
                    </div>
                  )}
                  {!kbLoading && !kbError && filteredModalDocs.length === 0 && (
                    <div className="kb-modal-state">
                      <p className="text-muted small">No documents found matching your active filters and toggles.</p>
                    </div>
                  )}
                  {!kbLoading && !kbError && filteredModalDocs.length > 0 && (
                    <div className="table-responsive">
                      <table className="table kb-select-table align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "60px", textAlign: "center" }}>
                              <input
                                type="checkbox"
                                className="custom-checkbox"
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDocs(filteredModalDocs.map((d) => d.id || d._id));
                                  } else {
                                    setSelectedDocs([]);
                                  }
                                }}
                                checked={filteredModalDocs.length > 0 && selectedDocs.length === filteredModalDocs.length}
                              />
                            </th>
                            <th>Document Name</th>
                            <th>Department</th>
                            <th>Source</th>
                            <th>Verification Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredModalDocs.map((doc) => {
                            const docId = doc.id || doc._id;
                            const isSelected = selectedDocs.includes(docId);
                            return (
                              <tr key={docId} className={isSelected ? "kb-row-selected" : ""}>
                                <td style={{ textAlign: "center" }}>
                                  <input
                                    type="checkbox"
                                    className="custom-checkbox"
                                    checked={isSelected}
                                    onChange={(e) => { e.stopPropagation(); handleToggleDoc(docId); }}
                                  />
                                </td>
                                <td className={isSelected ? "text-primary fw-medium" : "fw-medium"}>
                                  {doc.file_name || doc.name}
                                </td>
                                <td>{doc.department || "-"}</td>
                                <td>{doc.source_type || doc.source || "MANUAL"}</td>
                                <td>
                                  <span className={doc.verified ? "text-success fw-semibold" : "text-muted fw-semibold"}>
                                    {doc.verified ? "Verified" : "Not Verified"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="kb-modal-footer">
                  <span className="kb-selected-count">
                    {selectedDocs.length > 0
                      ? `${selectedDocs.length} document${selectedDocs.length > 1 ? "s" : ""} selected`
                      : "No documents selected"}
                  </span>
                  <div className="d-flex gap-2">
                    <button className="btn-kb-cancel" onClick={handleCloseKbModal}>Cancel</button>
                    <button className="btn-kb-attach" onClick={handleAttachDocs} disabled={selectedDocs.length === 0}>
                      Attach Selected
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>{/* end agent-creation-container */}
      </div>{/* end db-main-wrapper */}
    </div>  /* end db-page-layout */
  );
};

export default AgentCreation;